import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider, Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../supabaseClient'
import {
  J, ReflectionBlock, ViewToggle, handlePrint, fmtDate, fmtDateTime,
  JournalView, StepReflection, JournalNote,
} from '../../components/journal/paper'

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * View modes:
 *   summary — compact (mentor portal use-case; kept for future reuse)
 *   full    — curated journal: outcomes + student reflection + acknowledgments
 *             + saved AI notes. The print-ready research notebook a student
 *             takes to the fair. No token metrics, no legalistic disclosure.
 *   audit   — complete documentation: full system record, every Q&A, every
 *             rejection, gate-attempt history, token engagement metrics,
 *             ISEF Rule 4.2 disclosure. The student's backup proof of work.
 */

interface GateScore {
  name:        string
  specificity: number
  connection:  number
  effort:      number
  total:       number
}

interface TopicRec {
  id:                   string
  title:                string
  description:          string
  spanning_categories:  string[]
  decision:             'accept' | 'reject' | null
  justification:        string | null
  decided_at:           string | null
  generated_at:         string
  source:               'ai' | 'student'
  round:                number
  evaluation:           any | null
  finalist_thoughts?:   string | null
}

interface QAEntry {
  id:                      string
  topic_recommendation_id: string
  question:                string
  answer:                  string
  asked_at:                string
}


interface JournalData {
  grade:         string
  categoryInput: {
    selections:        Array<{ category_id: string; name: string; reason: string }>
    gate_scores:       GateScore[] | null
    gate_attempt_count:number
    submitted_at:      string
    created_at:        string
  } | null
  categories:    Record<string, { name: string; long_description: string | null }>
  topics:        TopicRec[]
  qaLog:         QAEntry[]
  researchTopic: {
    title:                         string | null
    final_justification:           string | null
    submitted_at:                  string | null
    status:                        string
    selected_recommendation_id:    string | null
    finalist_recommendation_ids:   string[] | null
    student_thoughts:              string | null
    mentor_note:                   string | null
  } | null
  mentorMeetingHeldAt: string | null
  reflections:   Record<number, StepReflection>
  notes:         Record<number, JournalNote[]>
  phases:        Record<string, string>
  tokenUsage:    Array<{ function_name: string; model: string; input_tokens: number; output_tokens: number }>
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const FUNCTION_LABELS: Record<string, string> = {
  'gate-category-input':           'Category Reflection Review',
  'recommend-topics':              'Topic Recommendation',
  'recommend-topics:fresh':        'Topic Recommendation — Fresh idea',
  'recommend-topics:harder':       'Topic Recommendation — Harder variation',
  'recommend-topics:simpler':      'Topic Recommendation — Simpler variation',
  'recommend-topics:different_angle': 'Topic Recommendation — Different angle',
  'recommend-topics:wonder':       'Wonder Prompts',
  'evaluate-student-topic':        'Student Topic Evaluation',
  'ask-about-topic':               'Topic Q&A',
  'compare-topics':                'Comparative Analysis (legacy)',
}
const PHASE_LABELS: Record<string, string> = {
  gate_passed: 'Categories Confirmed',
  topic_loop:  'Topic Exploration Began',
  comparison:  'Comparative Analysis',
  submitted:   'Submitted for Review',
}


// ── Category Journal ──────────────────────────────────────────────────────────

function CategoryJournal({ data, view }: { data: JournalData; view: JournalView }) {
  const ci = data.categoryInput
  if (!ci) return <Alert severity="info">Category selection not yet complete.</Alert>

  const date     = ci.submitted_at ? fmtDate(ci.submitted_at) : '—'
  const dateTime = ci.submitted_at ? fmtDateTime(ci.submitted_at) : '—'
  const usedFns  = Array.from(new Set(data.tokenUsage.filter(u =>
    u.function_name === 'gate-category-input'
  ).map(u => u.function_name)))

  return (
    <J.Page id="journal-category-print">
      <J.Header
        title={view === 'audit' ? 'STEP 1 DOCUMENTATION — CATEGORIES' : 'SCIENCE RESEARCH JOURNAL'}
        subtitle={view === 'audit' ? 'Complete record of category selection' : 'Step 1 — Research Interest Identification'}
        date={date}
      />

      {view === 'summary' && (
        <>
          <J.Body>
            After structured self-reflection, I selected the following research areas as the
            foundation for my science fair project. Confirmed by AI mentor review on {dateTime}.
          </J.Body>
          {ci.selections.map((sel, i) => (
            <Box key={sel.category_id} className="journal-no-break" sx={{ mb: 1.5 }}>
              <J.Body sx={{ fontWeight: 'bold', mb: 0 }}>{i + 1}. {sel.name}</J.Body>
              <J.Quote>{sel.reason}</J.Quote>
            </Box>
          ))}
        </>
      )}

      {(view === 'full' || view === 'audit') && (
        <>
          <J.Section>RESEARCH AREA IDENTIFICATION</J.Section>
          <J.Body>
            Through a structured review of research domains and guided self-reflection, I identified
            the following {ci.selections.length === 1 ? 'area' : `${ci.selections.length} areas`} where
            my personal experiences and curiosity align with opportunities for original investigation.
          </J.Body>

          {ci.selections.map((sel, i) => {
            const cat = data.categories[sel.category_id]
            return (
              <Box key={sel.category_id} className="journal-no-break" sx={{ mb: 2.5, pl: 1.5, borderLeft: '2px solid #ddd' }}>
                <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt', mb: 0.25 }}>
                  {i + 1}. {sel.name.toUpperCase()}
                </J.Body>
                {cat?.long_description && (
                  <J.Body sx={{ color: '#333', mb: 0.75, fontSize: '10.5pt' }}>
                    {cat.long_description}
                  </J.Body>
                )}
                <J.Label>Personal Connection</J.Label>
                <J.Quote>{sel.reason}</J.Quote>
              </Box>
            )
          })}

          <J.Section>REFLECTION REVIEW AND CONFIRMATION</J.Section>
          <J.Body>
            My category selections and personal connections were reviewed by an AI mentor for
            evidence of genuine reflection — whether each reason referenced a real experience,
            observation, or personal interest, rather than a generic statement.
            {ci.gate_attempt_count > 1
              ? ` I submitted my selections ${ci.gate_attempt_count} times before all met the reflection standard.`
              : ' All selections met the reflection standard on the first submission.'}
          </J.Body>
          <J.Body>Confirmed: {dateTime}</J.Body>

          {view === 'audit' && ci.gate_scores && ci.gate_scores.length > 0 && (
            <>
              <J.Section>REFLECTION ASSESSMENT SCORES</J.Section>
              <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>
                Each category reason was scored on three dimensions (1–3): specificity (concreteness),
                personal connection (personal anchoring), and personal angle (own story coming through).
                Minimum passing total: 6 of 9.
              </J.Body>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: '10pt', mb: 1.5 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ borderBottom: '2px solid #333' }}>
                    {['Category', 'Specificity', 'Connection', 'Personal angle', 'Total'].map(h => (
                      <Box component="th" key={h} sx={{ textAlign: h === 'Category' ? 'left' : 'center', pb: 0.5, fontWeight: 'bold', pr: h !== 'Total' ? 1 : 0 }}>
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {ci.gate_scores.map(s => (
                    <Box component="tr" key={s.name} sx={{ borderBottom: '1px solid #ddd' }}>
                      <Box component="td" sx={{ py: 0.5, pr: 1 }}>{s.name}</Box>
                      <Box component="td" sx={{ textAlign: 'center', py: 0.5 }}>{s.specificity ?? '—'}</Box>
                      <Box component="td" sx={{ textAlign: 'center', py: 0.5 }}>{s.connection ?? '—'}</Box>
                      <Box component="td" sx={{ textAlign: 'center', py: 0.5 }}>{s.effort ?? '—'}</Box>
                      <Box component="td" sx={{ textAlign: 'center', py: 0.5, fontWeight: 'bold' }}>{s.total}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {view === 'audit' && data.tokenUsage.length > 0 && (
            <>
              <J.Section>AI ENGAGEMENT METRICS</J.Section>
              <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>
                The following records AI processing volume for this entry. Token counts reflect the
                amount of context exchanged with the AI system.
              </J.Body>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: '10pt', mb: 1 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ borderBottom: '2px solid #333' }}>
                    {['AI Function', 'Model', 'Input Tokens', 'Output Tokens'].map(h => (
                      <Box component="th" key={h} sx={{ textAlign: h === 'AI Function' ? 'left' : 'right', pb: 0.5, fontWeight: 'bold', pr: 1 }}>
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {data.tokenUsage.filter(u => u.function_name === 'gate-category-input').map((u, i) => (
                    <Box component="tr" key={i} sx={{ borderBottom: '1px solid #ddd' }}>
                      <Box component="td" sx={{ py: 0.5, pr: 1 }}>{FUNCTION_LABELS[u.function_name] ?? u.function_name}</Box>
                      <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1, fontSize: '9pt', color: '#555' }}>{u.model}</Box>
                      <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1 }}>{u.input_tokens.toLocaleString()}</Box>
                      <Box component="td" sx={{ textAlign: 'right', py: 0.5 }}>{u.output_tokens.toLocaleString()}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {view === 'audit' && (
            <J.Disclosure functions={usedFns} date={date} full={true} labels={FUNCTION_LABELS} />
          )}
        </>
      )}
    </J.Page>
  )
}

// ── Topic Journal ─────────────────────────────────────────────────────────────

function TopicJournal({ data, view }: { data: JournalData; view: JournalView }) {
  const rt = data.researchTopic
  if (!rt || rt.status === 'draft' || !rt.finalist_recommendation_ids) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        This entry will be available once you submit your finalists for mentor review.
      </Alert>
    )
  }

  const accepted     = data.topics.filter(t => t.decision === 'accept')
  const rejected     = data.topics.filter(t => t.decision === 'reject')
  const finalistIds  = rt.finalist_recommendation_ids ?? []
  const finalists    = finalistIds.map(id => accepted.find(t => t.id === id)).filter(Boolean) as TopicRec[]
  const approved     = data.topics.find(t => t.id === rt.selected_recommendation_id) ?? null
  const isApproved   = rt.status === 'completed' && approved
  const loopStart    = data.phases['topic_loop']
  const submitted    = data.phases['submitted'] ?? rt.submitted_at

  const dateStart   = loopStart ? fmtDate(loopStart) : '—'
  const dateEnd     = submitted ? fmtDate(submitted) : '—'
  const dateDisplay = dateStart === dateEnd ? dateEnd : `${dateStart} – ${dateEnd}`
  const reflection  = data.reflections[1] ?? null
  const stepNotes   = data.notes[1] ?? []

  const topicFunctions = Array.from(new Set(data.tokenUsage
    .filter(u => u.function_name !== 'gate-category-input')
    .map(u => u.function_name)))

  return (
    <J.Page id="journal-topic-print">
      <J.Header
        title={view === 'audit' ? 'STEP 1 DOCUMENTATION — TOPICS' : 'SCIENCE RESEARCH JOURNAL'}
        subtitle={view === 'audit' ? 'Complete record of topic exploration' : 'Step 1 — Research Topic Selection'}
        date={dateDisplay}
      />

      {view === 'summary' && (
        <>
          <J.Body>
            After exploring {data.topics.length} research topic candidate{data.topics.length !== 1 ? 's' : ''},
            I submitted {finalists.length} finalist{finalists.length !== 1 ? 's' : ''} for mentor review.
            {isApproved && approved && ` My approved topic: ${approved.title}.`}
          </J.Body>

          {finalists.map((t, i) => (
            <Box key={t.id} className="journal-no-break" sx={{ mb: 1.5 }}>
              <J.Body sx={{ fontWeight: 'bold', mb: 0 }}>
                {i + 1}. {t.title}
                {t.id === approved?.id && (
                  <Box component="span" sx={{ ml: 1, fontSize: '9pt', fontStyle: 'italic', color: '#1B5E20' }}>
                    (approved)
                  </Box>
                )}
              </J.Body>
              {t.finalist_thoughts && <J.Quote>{t.finalist_thoughts}</J.Quote>}
            </Box>
          ))}
        </>
      )}

      {/* ── Journal view: topics considered, then selected topic post-approval ─ */}
      {view === 'full' && (
        <>
          <J.Section>RESEARCH TOPICS I CONSIDERED</J.Section>
          <J.Body>
            During Step 1, I explored and considered the following research topics:
          </J.Body>

          {accepted.map(t => (
            <Box key={t.id} className="journal-no-break" sx={{ mb: 1.75, pl: 1.5, borderLeft: '2px solid #ddd' }}>
              <J.Body sx={{ fontWeight: 'bold', fontSize: '11.5pt', mb: 0.25 }}>{t.title}</J.Body>
              {t.description && (
                <Typography sx={{
                  fontFamily: 'inherit', fontSize: '10.5pt', color: '#333',
                  lineHeight: 1.6, whiteSpace: 'pre-wrap', mb: 0,
                }}>
                  {t.description}
                </Typography>
              )}
            </Box>
          ))}

          {isApproved && approved ? (
            <>
              <J.Section>SELECTED RESEARCH TOPIC FOR STEP 2</J.Section>
              <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt', mb: 0.5 }}>{approved.title}</J.Body>
              <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>{approved.description}</J.Body>
              {approved.finalist_thoughts && (
                <>
                  <J.Label>Why I chose this topic</J.Label>
                  <J.Quote>{approved.finalist_thoughts}</J.Quote>
                </>
              )}
            </>
          ) : (
            <J.Body sx={{ fontSize: '10pt', color: '#555', fontStyle: 'italic' }}>
              {submitted
                ? `Submitted to my mentor on ${fmtDate(submitted)}. The final topic will be recorded here once mentor review is complete.`
                : 'Final topic will be recorded here once mentor review is complete.'}
            </J.Body>
          )}

          <ReflectionBlock refl={reflection} notes={stepNotes} />
        </>
      )}

      {/* ── Documentation view: full process record ─ */}
      {view === 'audit' && (
        <>
          <J.Section>RESEARCH TOPIC EXPLORATION</J.Section>
          <J.Body>
            Beginning {loopStart ? fmtDateTime(loopStart) : 'the exploration phase'}, I explored
            research topics by proposing my own ideas and asking the AI Coach for an opinion on each
            one's feasibility as a science fair project. I treated the AI's read as informational
            input I weighed alongside my own judgment, not as a verdict.
            {data.topics.some(t => t.source === 'ai') && (
              <> When I wanted to broaden my thinking, I also browsed AI-generated suggestions and
              asked for variations (a fresh idea, a harder version, a simpler version, or a different
              angle on the same area).</>
            )}
            {data.topics.filter(t => t.source === 'student').length > 0 && data.topics.filter(t => t.source === 'ai').length > 0 && (
              <> Of {data.topics.length} topics considered, {data.topics.filter(t => t.source === 'student').length} were
              proposed by me and {data.topics.filter(t => t.source === 'ai').length} came from the AI Coach.</>
            )}
            {data.topics.filter(t => t.source === 'student').length > 0 && data.topics.filter(t => t.source === 'ai').length === 0 && (
              <> All {data.topics.filter(t => t.source === 'student').length} topic{data.topics.filter(t => t.source === 'student').length !== 1 ? 's' : ''} I considered came from my own thinking.</>
            )}
          </J.Body>

          <J.Label>Accepted Candidates ({accepted.length})</J.Label>
          {accepted.map((t, i) => {
            const qa = data.qaLog.filter(q => q.topic_recommendation_id === t.id)
            return (
              <Box key={t.id} className="journal-no-break" sx={{ mb: 2, pl: 1.5, borderLeft: '2px solid #aaa' }}>
                <J.Body sx={{ fontWeight: 'bold', mb: 0 }}>
                  {i + 1}. {t.title}
                  {t.source === 'student' && (
                    <Box component="span" sx={{ ml: 1, fontSize: '9pt', fontStyle: 'italic', color: '#666' }}>
                      (student-proposed)
                    </Box>
                  )}
                </J.Body>
                <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 0.5 }}>{t.description}</J.Body>
                {t.justification && (
                  <>
                    <J.Label>Why I accepted this</J.Label>
                    <J.Quote>{t.justification}</J.Quote>
                  </>
                )}
                {t.decided_at && (
                  <J.Body sx={{ fontSize: '9pt', color: '#666' }}>Accepted: {fmtDateTime(t.decided_at)}</J.Body>
                )}

                {qa.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <J.Label>Questions I asked before deciding</J.Label>
                    {qa.map(q => (
                      <Box key={q.id} sx={{ mb: 0.75 }}>
                        <J.Body sx={{ fontWeight: 'bold', fontSize: '10pt', mb: 0 }}>Q: {q.question}</J.Body>
                        <J.Body sx={{ fontSize: '10pt', color: '#333', mb: 0 }}>A: {q.answer}</J.Body>
                      </Box>
                    ))}
                  </Box>
                )}

                {t.source === 'student' && t.evaluation && (
                  <Box sx={{ mt: 1 }}>
                    <J.Label>AI Feasibility Evaluation</J.Label>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 0.75 }}>
                      {(['time_fit','budget','equipment','skills','specificity'] as const).map(dim => {
                        const d = t.evaluation?.[dim]
                        if (!d) return null
                        const labelMap: Record<string, string> = {
                          time_fit: 'Time fit', budget: 'Budget', equipment: 'Equipment',
                          skills: 'Skills', specificity: 'Specificity',
                        }
                        return (
                          <Box key={dim}>
                            <J.Body sx={{ fontWeight: 'bold', fontSize: '9.5pt', mb: 0 }}>{labelMap[dim]}: {(d.state ?? d.rating ?? '').toUpperCase()}</J.Body>
                            <J.Body sx={{ fontSize: '9.5pt', color: '#444', mb: 0 }}>{d.note ?? d.explanation ?? ''}</J.Body>
                          </Box>
                        )
                      })}
                    </Box>
                    {t.evaluation.step2_hint && (
                      <J.Body sx={{ fontSize: '10pt', fontStyle: 'italic' }}>{t.evaluation.step2_hint}</J.Body>
                    )}
                  </Box>
                )}
              </Box>
            )
          })}

          {rejected.length > 0 && (
            <>
              <J.Label>Topics Considered and Declined ({rejected.length})</J.Label>
              {rejected.map(t => (
                <Box key={t.id} className="journal-no-break" sx={{ mb: 1.25, pl: 1.5, borderLeft: '2px solid #ddd' }}>
                  <J.Body sx={{ fontWeight: 'bold', mb: 0, color: '#555' }}>{t.title}</J.Body>
                  {t.justification && <J.Quote>{t.justification}</J.Quote>}
                  {t.decided_at && (
                    <J.Body sx={{ fontSize: '9pt', color: '#888' }}>Declined: {fmtDateTime(t.decided_at)}</J.Body>
                  )}
                </Box>
              ))}
            </>
          )}
        </>
      )}

      {/* ── Documentation-only: finalists submitted, mentor feedback, approval ─ */}
      {view === 'audit' && (
        <>
          <J.Section>FINALISTS SUBMITTED FOR MENTOR REVIEW</J.Section>
          <J.Body>
            I submitted {finalists.length} finalist{finalists.length !== 1 ? 's' : ''}
            {finalists.length === 1 ? ' for mentor approval' : ' for mentor discussion'}, with my
            reasoning for each. {submitted && `Submitted: ${fmtDateTime(submitted)}.`}
          </J.Body>

          {finalists.map((t, i) => (
            <Box key={t.id} className="journal-no-break" sx={{ mb: 2, pl: 1.5, borderLeft: '2px solid ' + (t.id === approved?.id ? '#2e7d32' : '#aaa') }}>
              <J.Body sx={{ fontWeight: 'bold', mb: 0.25 }}>
                {i + 1}. {t.title}
                {t.id === approved?.id && (
                  <Box component="span" sx={{ ml: 1, fontSize: '9pt', fontStyle: 'italic', color: '#1B5E20' }}>
                    (approved by mentor)
                  </Box>
                )}
              </J.Body>
              {t.finalist_thoughts && (
                <>
                  <J.Label>Why this one — and how I'd handle the AI's concerns</J.Label>
                  <J.Quote>{t.finalist_thoughts}</J.Quote>
                </>
              )}
            </Box>
          ))}

          {rt.mentor_note && (
            <>
              <J.Label>Mentor feedback</J.Label>
              <J.Quote>{rt.mentor_note}</J.Quote>
              {data.mentorMeetingHeldAt && (
                <J.Body sx={{ fontSize: '9pt', color: '#666' }}>
                  Mentor meeting held: {fmtDateTime(data.mentorMeetingHeldAt)}
                </J.Body>
              )}
            </>
          )}

          {isApproved && approved && (
            <>
              <J.Section>APPROVED RESEARCH TOPIC</J.Section>
              <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt' }}>{approved.title}</J.Body>
              <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>{approved.description}</J.Body>
              {approved.finalist_thoughts && (
                <>
                  <J.Label>My reasoning</J.Label>
                  <J.Quote>{approved.finalist_thoughts}</J.Quote>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ── Documentation-only: phase timeline + token metrics + ISEF disclosure ─ */}
      {view === 'audit' && (
        <>
              <J.Section>PROCESS TIMELINE</J.Section>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: '10pt', mb: 1 }}>
                <Box component="tbody">
                  {Object.entries(data.phases).map(([phase, ts]) => (
                    <Box component="tr" key={phase} sx={{ borderBottom: '1px solid #eee' }}>
                      <Box component="td" sx={{ py: 0.5, pr: 2, fontWeight: 'bold' }}>{PHASE_LABELS[phase] ?? phase}</Box>
                      <Box component="td" sx={{ py: 0.5 }}>{fmtDateTime(ts)}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {data.tokenUsage.filter(u => u.function_name !== 'gate-category-input').length > 0 && (
                <>
                  <J.Section>AI ENGAGEMENT METRICS</J.Section>
                  <J.Body sx={{ fontSize: '10pt', color: '#444', mb: 1 }}>
                    Token counts reflect context exchanged with the AI Coach during topic exploration.
                  </J.Body>
                  {(() => {
                    const byFn: Record<string, { input: number; output: number }> = {}
                    data.tokenUsage.filter(u => u.function_name !== 'gate-category-input').forEach(u => {
                      if (!byFn[u.function_name]) byFn[u.function_name] = { input: 0, output: 0 }
                      byFn[u.function_name].input  += u.input_tokens
                      byFn[u.function_name].output += u.output_tokens
                    })
                    return (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit', fontSize: '10pt', mb: 1 }}>
                        <Box component="thead">
                          <Box component="tr" sx={{ borderBottom: '2px solid #333' }}>
                            {['AI Function', 'Input Tokens', 'Output Tokens', 'Total'].map(h => (
                              <Box component="th" key={h} sx={{ textAlign: h === 'AI Function' ? 'left' : 'right', pb: 0.5, pr: 1, fontWeight: 'bold' }}>{h}</Box>
                            ))}
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {Object.entries(byFn).map(([fn, counts]) => (
                            <Box component="tr" key={fn} sx={{ borderBottom: '1px solid #eee' }}>
                              <Box component="td" sx={{ py: 0.5, pr: 1 }}>{FUNCTION_LABELS[fn] ?? fn}</Box>
                              <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1 }}>{counts.input.toLocaleString()}</Box>
                              <Box component="td" sx={{ textAlign: 'right', py: 0.5, pr: 1 }}>{counts.output.toLocaleString()}</Box>
                              <Box component="td" sx={{ textAlign: 'right', py: 0.5, fontWeight: 'bold' }}>{(counts.input + counts.output).toLocaleString()}</Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )
                  })()}
                </>
              )}

              <J.Disclosure functions={topicFunctions} date={dateEnd} full={true} labels={FUNCTION_LABELS} />
            </>
          )}
    </J.Page>
  )
}


// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  projectId: string
  studentId: string
  isTrial?:  boolean
  /** Default true (the student is viewing their own journal). When false the
   *  outer copy switches to mentor-facing third-person voice. */
  viewerIsStudent?: boolean
}

export default function JournalTab({ projectId, studentId, isTrial = false, viewerIsStudent = true }: Props) {
  const [data,    setData]    = useState<JournalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [view,    setView]    = useState<'journal' | 'documentation'>('journal')

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [
          ciRes, catRes, topicRes, rtRes, profileRes, phaseRes, usageRes, qaRes,
          reflectionRes, notesRes,
        ] = await Promise.all([
          supabase.from('category_input').select('*').eq('project_id', projectId).maybeSingle(),
          supabase.from('curated_category').select('id,name,long_description'),
          supabase.from('topic_recommendation').select('*').eq('project_id', projectId).order('generated_at'),
          supabase.from('research_topic').select('*').eq('project_id', projectId).maybeSingle(),
          supabase.from('student_profile').select('grade').eq('student_id', studentId).maybeSingle(),
          supabase.from('step1_phase_log').select('*').eq('project_id', projectId),
          supabase.from('ai_usage_log').select('*').eq('project_id', projectId),
          supabase.from('topic_qa_log').select('*').eq('project_id', projectId).order('asked_at'),
          supabase.from('step_reflection').select('*').eq('project_id', projectId),
          supabase.from('journal_note').select('*').eq('project_id', projectId).order('saved_at'),
        ])

        // Latest held step1_review meeting (for the "Mentor meeting held" line)
        let mentorMeetingHeldAt: string | null = null
        if (rtRes.data?.id) {
          const { data: held } = await supabase
            .from('meeting')
            .select('held_at')
            .eq('research_topic_id', rtRes.data.id)
            .eq('kind', 'step1_review')
            .eq('status', 'held')
            .order('held_at', { ascending: false })
            .limit(1)
            .maybeSingle()
          mentorMeetingHeldAt = (held as any)?.held_at ?? null
        }

        const catMap: Record<string, { name: string; long_description: string | null }> = {}
        for (const c of catRes.data ?? []) catMap[c.id] = { name: c.name, long_description: c.long_description }

        const phases: Record<string, string> = {}
        for (const p of phaseRes.data ?? []) phases[p.phase] = p.started_at

        const reflections: Record<number, StepReflection> = {}
        for (const r of reflectionRes.data ?? []) reflections[r.step_number] = r as StepReflection

        const notes: Record<number, JournalNote[]> = {}
        for (const n of notesRes.data ?? []) {
          if (!notes[n.step_number]) notes[n.step_number] = []
          notes[n.step_number].push(n as JournalNote)
        }

        setData({
          grade:         profileRes.data?.grade ?? 'unknown',
          categoryInput: ciRes.data ?? null,
          categories:    catMap,
          topics:        (topicRes.data ?? []) as TopicRec[],
          qaLog:         (qaRes.data ?? []) as QAEntry[],
          researchTopic: rtRes.data ?? null,
          mentorMeetingHeldAt,
          reflections,
          notes,
          phases,
          tokenUsage:    usageRes.data ?? [],
        })
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectId, studentId])

  if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
  if (error)   return <Alert severity="error">{error}</Alert>
  if (!data)   return null

  // Map top-level view to per-section view (documentation = audit on both)
  const innerView: JournalView = view === 'documentation' ? 'audit' : 'full'
  const printIdCategory = view === 'documentation' ? 'journal-category-print' : 'journal-category-print'
  const printIdTopic    = view === 'documentation' ? 'journal-topic-print' : 'journal-topic-print'

  return (
    <Box>
      {/* Top toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {viewerIsStudent
              ? (view === 'journal' ? 'Your Research Journal' : 'Research Documentation')
              : (view === 'journal' ? "Student's Journal" : "Student's Documentation")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {viewerIsStudent
              ? (view === 'journal'
                  ? 'A curated record of what you decided, what you learned, and who helped you. This is what you bring to a science fair or share with your mentor.'
                  : 'Complete system record of your work — every Q&A, every score, every revision. Use this if asked to document your AI use under ISEF Rule 4.2 or for instructor audit.')
              : (view === 'journal'
                  ? "The student's curated record — categories, topics considered, the selected topic with reasoning, and per-step reflections."
                  : 'Complete system record — every Q&A, every score, all rejected topics, phase timeline, and AI engagement metrics.')}
          </Typography>
        </Box>
        <ViewToggle value={view} onChange={setView} />
      </Box>

      {/* ── Journal Entry 1: Categories ── */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Step 1A — Research Interest Identification
          </Typography>
          <Button
            variant="outlined" size="small"
            startIcon={<PrintIcon />}
            onClick={() => handlePrint(printIdCategory)}
            disabled={!data.categoryInput}
          >
            Print
          </Button>
        </Box>
        <CategoryJournal data={data} view={innerView} />
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* ── Journal Entry 2: Topic ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Step 1B — Research Topic Selection
          </Typography>
          {!isTrial && (
            <Button
              variant="outlined" size="small"
              startIcon={<PrintIcon />}
              onClick={() => handlePrint(printIdTopic)}
              disabled={!data.researchTopic || data.researchTopic.status === 'draft'}
            >
              Print
            </Button>
          )}
        </Box>
        {isTrial ? (
          <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
            <Box sx={{ filter: 'blur(3px)', opacity: 0.4, pointerEvents: 'none', userSelect: 'none' }}>
              <TopicJournal data={data} view="summary" />
            </Box>
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(249,247,244,0.75)', borderRadius: 2, p: 4, textAlign: 'center',
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Full topic journal unlocks in the complete program
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 380 }}>
                Once enrolled, your topic-selection process — finalists, mentor review, and final
                approval — becomes part of your permanent research record.
              </Typography>
              <Button
                variant="contained"
                href="https://ardentresearch.org"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 700 }}
              >
                Apply to Ardent Research
              </Button>
            </Box>
          </Box>
        ) : (
          <TopicJournal data={data} view={innerView} />
        )}
      </Box>

      {/* Signatures live at the bottom — once for the whole journal, journal view only */}
      {view === 'journal' && data.researchTopic?.status === 'completed' && (
        <Box sx={{ mt: 2 }}>
          <J.Page id="journal-signatures-print">
            <J.Signatures />
          </J.Page>
        </Box>
      )}
    </Box>
  )
}
